import { useFetchCompletedForms } from '@/api/hooks/useFetchCompletedForms'
import { useFetchLatestAppoinmentResults } from '@/api/hooks/useFetchLatestAppointmentResults'
import { useFetchPendingForms } from '@/api/hooks/useFetchPendingForms'
import { Forms } from '@/components/CarePage/Forms'
import { ScoreCard } from '@/components/CarePage/ScoreCard'
import { VisitsSection } from '@/components/CarePage/VisitsSection'
import { TabScreenHeader } from '@/components/TabScreenHeader'
import { readingsOf, useVisitRecords } from '@/features/care/useVisitRecords'
import { PageShell } from '@/ui/core/page-shell'
import { Screen } from '@/ui/core/screen'
import { VStack } from '@/ui/core/stack'
import Images from '#/assets/images/image-map'

/**
 * Care, top to bottom: the score's last four readings; the visits behind them, each a page of its
 * own; and every form, to do and done, with a sheet for all of them.
 *
 * One source for the visits and the chart, so the two cannot disagree — see `useVisitRecords`,
 * which is where the history endpoint will land. With nothing to show the section draws nothing
 * rather than an empty table, and the card wears its "No Score Yet" pill.
 */

export default function CareScreen() {
	const { pendingForms, isLoading: isLoadingPending } = useFetchPendingForms()
	const { hasAppointmentResults, appointmentResults, appointmentId, isLoadingResults } =
		useFetchLatestAppoinmentResults()
	const { visits } = useVisitRecords()
	const {
		completedForms,
		isLoading: isLoadingCompleted,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useFetchCompletedForms()

	return (
		<Screen preset="fixed" className="flex flex-1" safeAreaEdges={[]}>
			<PageShell photo={Images.BrandPhoto} contentClassName="pt-2">
				<TabScreenHeader />
				{/* Twenty-four rather than twenty. Two of the three blocks below open with a small
				    caption rather than a card edge, and type needs more air above it than a corner
				    does — at twenty the label sat closer to the card it followed than to the one it
				    named. */}
				<VStack gap={24}>
					{/* One reading, until the history endpoint lands: the latest result is all the
					    app can read, and it carries no date, so its bar is drawn unlabelled and the
					    three slots beside it climb from it. The same card draws the full series the
					    day the rest arrive — `readingsOf` is what it will be built from, and
					    nothing here changes but the length of that list. */}
					<ScoreCard
						readings={
							hasAppointmentResults && appointmentResults && appointmentId
								? [{ appointmentId, score: appointmentResults.healthScore }]
								: readingsOf(visits)
						}
						isLoading={isLoadingResults}
					/>
					{/* Where Health Records promised everything and held nothing. The visits are
					    real content for the same slot, and the promise is two lines under the
					    caption. Three of them, with See All behind the rest. */}
					<VisitsSection visits={visits} />
					<Forms
						pending={pendingForms}
						completed={completedForms}
						isLoading={isLoadingPending || isLoadingCompleted}
						hasMore={!!hasNextPage}
						isFetchingMore={isFetchingNextPage}
						onMore={() => fetchNextPage()}
					/>
				</VStack>
			</PageShell>
		</Screen>
	)
}
